// src/pages/ResetPassword.tsx
import { LockClosedIcon } from "@heroicons/react/24/outline"
import { LoversApi } from "api/LoversApi"
import { AxiosError } from "axios"
import { WithSimpleLogoHeader } from "components/WithSimpleLogoHeader"
import { Alert, Button, TextField } from "components/ui"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

interface ResetPasswordForm {
  password: string
  confirmPassword: string
}

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [form, setForm] = useState<ResetPasswordForm>({
    password: "",
    confirmPassword: ""
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const code = searchParams.get("code")

    if (!code) {
      setError("Reset code is missing")
      return
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match")
      return
    }

    if (form.password.length < 6) {
      setError("Password must be at least 8 characters")
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await LoversApi().forgotPassword.updatePassword({
        newPassword: form.password,
        updateCode: code
      })
      setSuccess(true)
    } catch (err) {
      if (err instanceof AxiosError) {
        setError(err.response?.data.errorMessage || "Failed to reset password")
      }
      setError(err instanceof Error ? err.message : "Failed to reset password")
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  if (success) {
    return (
      <WithSimpleLogoHeader>
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
            <LockClosedIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
            Password Reset Successfully!
          </h2>
          <p className="mt-2 px-4 text-gray-600 dark:text-gray-300">
            Your password has been updated. You can now log in with your new password.
          </p>
          <Button onClick={() => navigate("/login")} className="mt-6">
            Go to Login
          </Button>
        </div>
      </WithSimpleLogoHeader>
    )
  }

  return (
    <WithSimpleLogoHeader>
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold leading-9 tracking-tight text-gray-900 dark:text-white">
            Reset your password
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Please enter your new password below
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && <Alert variant="error" title={error} />}
          <div className="space-y-6 rounded-md shadow-sm">
            <TextField
              label="New Password"
              id="password"
              name="password"
              type="password"
              required
              value={form.password}
              onChange={handleChange}
            />

            <TextField
              label="Confirm New Password"
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              required
              value={form.confirmPassword}
              onChange={handleChange}
            />
          </div>

          <div>
            <Button type="submit" fullWidth isLoading={isLoading}>
              {isLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </div>
        </form>
      </div>
    </WithSimpleLogoHeader>
  )
}
